import { useState, useRef, useCallback, useMemo, memo } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import {
  Select, MenuItem, TextField, FormControl, InputLabel,
  Button, ToggleButton, ToggleButtonGroup,
  Slider, Switch, FormControlLabel, Chip, IconButton, Tabs, Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// ─────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────
const FONT_SIZES      = [8,9,10,11,12,13,14,16,18,20,24,28,32];
const FONT_FAMILIES   = ["sans-serif","serif","monospace","Arial","Roboto","Georgia"];
const FONT_STYLES     = ["normal","italic","bold","bold italic"];
const TEXT_ALIGNS     = ["left","center","right","start","end"];
const TEXT_BASELINES  = ["alphabetic","top","bottom","middle","hanging","ideographic"];
const TEXT_PLACEMENTS = ["point","line"];
const LINE_CAPS       = ["round","butt","square"];
const LINE_JOINS      = ["round","bevel","miter"];

const DEFAULT_VALUES = {
  // symbology
  fillColor:   "rgba(255,255,255,0.4)",
  strokeColor: "#3399CC",
  strokeWidth: 1.25,
  circleRadius: 5,
  lineCap:     "round",
  lineJoin:    "round",
  dashEnabled: false,
  dashLength:  10,
  dashGap:     5,
  // labelling
  lblEnabled:    false,
  lblMode:       "attribute",
  lblTextValue:  "",
  fontFamily:    "sans-serif",
  fontSize:      12,
  fontStyle:     "normal",
  fontColor:     "#333333",
  textAlign:     "center",
  baseline:      "middle",
  placement:     "point",
  offsetX:       0,
  offsetY:       0,
  rotation:      0,
  haloEnabled:   false,
  haloColor:     "#ffffff",
  haloWidth:     2,
  overflow:      false,
};

// ─────────────────────────────────────────────────────────
// FLAT STYLE BUILDER — reads plain values object
// ─────────────────────────────────────────────────────────
const buildFlatStyle = (geomType, v) => {
  const style = {};
  const gt = (geomType || "").toLowerCase();

  if (gt === "point") {
    style["circle-radius"]       = v.circleRadius;
    style["circle-fill-color"]   = v.fillColor;
    style["circle-stroke-color"] = v.strokeColor;
    style["circle-stroke-width"] = v.strokeWidth;
  } else if (gt === "linestring" || gt === "multilinestring") {
    style["stroke-color"]     = v.strokeColor;
    style["stroke-width"]     = v.strokeWidth;
    style["stroke-line-cap"]  = v.lineCap;
    style["stroke-line-join"] = v.lineJoin;
    if (v.dashEnabled) style["stroke-line-dash"] = [v.dashLength, v.dashGap];
  } else {
    style["fill-color"]       = v.fillColor;
    style["stroke-color"]     = v.strokeColor;
    style["stroke-width"]     = v.strokeWidth;
    style["stroke-line-cap"]  = v.lineCap;
    style["stroke-line-join"] = v.lineJoin;
    if (v.dashEnabled) style["stroke-line-dash"] = [v.dashLength, v.dashGap];
  }

  if (v.lblEnabled && v.lblTextValue) {
    let textVal;
    if (v.lblMode === "attribute")       textVal = ["get", v.lblTextValue];
    else if (v.lblMode === "expression") { try { textVal = JSON.parse(v.lblTextValue); } catch { textVal = v.lblTextValue; } }
    else                                  textVal = v.lblTextValue;

    style["text-value"]        = textVal;
    style["text-font"]         = `${v.fontStyle} ${v.fontSize}px ${v.fontFamily}`;
    style["text-fill-color"]   = v.fontColor;
    style["text-stroke-color"] = v.haloEnabled ? v.haloColor : "transparent";
    style["text-stroke-width"] = v.haloEnabled ? v.haloWidth : 0;
    style["text-align"]        = v.textAlign;
    style["text-baseline"]     = v.baseline;
    style["text-placement"]    = v.placement;
    style["text-offset-x"]     = v.offsetX;
    style["text-offset-y"]     = v.offsetY;
    style["text-overflow"]     = v.overflow;
    style["text-rotation"]     = (v.rotation * Math.PI) / 180;
  }
  return style;
};

// ─────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────
const getColumns = (vectorLayer) => {
  if (!vectorLayer) return [];
  const features = vectorLayer.getSource().getFeatures();
  if (!features.length) return [];
  const keys = new Set();
  features.forEach(f =>
    Object.keys(f.getProperties()).forEach(k => { if (k !== "geometry") keys.add(k); })
  );
  return Array.from(keys);
};

const savedToValues = (saved) => {
  if (!saved) return { ...DEFAULT_VALUES };
  // saved may be flat (new format) or {sym,lbl} (old format) — handle both
  if (saved.sym || saved.lbl) {
    // old format: remap lbl keys to form keys
    const lbl = saved.lbl ?? {};
    return {
      ...DEFAULT_VALUES,
      ...saved.sym,
      lblEnabled:   lbl.enabled   ?? DEFAULT_VALUES.lblEnabled,
      lblMode:      lbl.mode      ?? DEFAULT_VALUES.lblMode,
      lblTextValue: lbl.textValue ?? DEFAULT_VALUES.lblTextValue,
      fontFamily:   lbl.fontFamily  ?? DEFAULT_VALUES.fontFamily,
      fontSize:     lbl.fontSize    ?? DEFAULT_VALUES.fontSize,
      fontStyle:    lbl.fontStyle   ?? DEFAULT_VALUES.fontStyle,
      fontColor:    lbl.fontColor   ?? DEFAULT_VALUES.fontColor,
      textAlign:    lbl.textAlign   ?? DEFAULT_VALUES.textAlign,
      baseline:     lbl.baseline    ?? DEFAULT_VALUES.baseline,
      placement:    lbl.placement   ?? DEFAULT_VALUES.placement,
      offsetX:      lbl.offsetX     ?? DEFAULT_VALUES.offsetX,
      offsetY:      lbl.offsetY     ?? DEFAULT_VALUES.offsetY,
      rotation:     lbl.rotation    ?? DEFAULT_VALUES.rotation,
      haloEnabled:  lbl.haloEnabled ?? DEFAULT_VALUES.haloEnabled,
      haloColor:    lbl.haloColor   ?? DEFAULT_VALUES.haloColor,
      haloWidth:    lbl.haloWidth   ?? DEFAULT_VALUES.haloWidth,
      overflow:     lbl.overflow    ?? DEFAULT_VALUES.overflow,
    };
  }
  // new flat format — merge directly
  return { ...DEFAULT_VALUES, ...saved };
};

// ─────────────────────────────────────────────────────────
// DRAG
// ─────────────────────────────────────────────────────────
const useDrag = (dialogRef) => {
  const posRef = useRef({ x: 80, y: 80 });
  return useCallback((e) => {
    e.preventDefault();
    const ox = e.clientX - posRef.current.x;
    const oy = e.clientY - posRef.current.y;
    const onMove = (ev) => {
      posRef.current = { x: ev.clientX - ox, y: ev.clientY - oy };
      if (dialogRef.current)
        dialogRef.current.style.transform = `translate(${posRef.current.x}px,${posRef.current.y}px)`;
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);
};

// ─────────────────────────────────────────────────────────
// STATIC STYLES
// ─────────────────────────────────────────────────────────
const S = {
  dialog:{
    position:"absolute", top:0, left:0,
    transform:"translate(80px,80px)",
    width:360, zIndex:1500, borderRadius:6, overflow:"hidden",
    boxShadow:"0 8px 32px rgba(0,0,0,0.22),0 2px 8px rgba(0,0,0,0.12)",
    background:"#fafafa", display:"flex", flexDirection:"column",
    maxHeight:"82vh", userSelect:"none", border:"1px solid rgba(0,0,0,0.10)",
  },
  titleBar:{
    display:"flex", alignItems:"center", padding:"0 8px 0 14px", minHeight:46,
    background:"linear-gradient(135deg,#1565c0 0%,#1976d2 100%)",
    cursor:"move", flexShrink:0,
  },
  titleText:{ fontSize:13, fontWeight:700, color:"#fff", flex:1, pointerEvents:"none" },
  titleSub:{ opacity:0.75, fontWeight:400, fontSize:12, marginLeft:8 },
  closeBtn:{ color:"#fff", padding:4 },
  tabPanel:{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column", userSelect:"text", background:"#fff" },
  tabSlot:{ display:"flex", flex:1, flexDirection:"column", overflow:"hidden" },
  tabSlotHidden:{ display:"none", flex:1, flexDirection:"column", overflow:"hidden" },
  actionBar:{
    display:"flex", justifyContent:"flex-end", gap:8,
    padding:"10px 14px", background:"#f5f7fa",
    borderTop:"1px solid rgba(0,0,0,0.08)", flexShrink:0,
  },
  scrollBox:{ padding:"10px 14px", overflowY:"auto", flex:1 },
  sec:{
    display:"block", fontWeight:700, fontSize:10, color:"#888",
    textTransform:"uppercase", letterSpacing:1, marginBottom:4, marginTop:14,
    borderBottom:"1px solid #eee", paddingBottom:2,
  },
  row:{ display:"flex", gap:8, alignItems:"center", marginBottom:8 },
  lbl:{ width:88, flexShrink:0, fontSize:12, color:"#555" },
  colorPicker:{ width:32, height:28, border:"1px solid #ddd", borderRadius:4, cursor:"pointer", padding:2, flexShrink:0 },
  btnText:{ fontSize:12, textTransform:"none" },
  btnOk:{ fontSize:12, textTransform:"none", background:"#1976d2" },
};

const Sec = ({ c }) => <span style={S.sec}>{c}</span>;
const Row = ({ children }) => <div style={S.row}>{children}</div>;
const Lbl = ({ c }) => <span style={S.lbl}>{c}</span>;

// ─────────────────────────────────────────────────────────
// SYMBOLOGY TAB
// ─────────────────────────────────────────────────────────
const SymbologyTab = memo(({ geomType, control }) => {
  const gt      = (geomType || "").toLowerCase();
  const isPoint = gt === "point";
  const isLine  = gt === "linestring" || gt === "multilinestring";

  // only watch structural toggles that show/hide sections
  const dashEnabled = useWatch({ control, name: "dashEnabled" });

  return (
    <div style={S.scrollBox}>
      {!isLine && (<>
        <Sec c="Fill" />
        <Row>
          <Lbl c="Color" />
          <Controller name="fillColor" control={control} render={({ field }) => (
            <input type="color" style={S.colorPicker}
              defaultValue={field.value?.slice(0,7)}
              onBlur={e => field.onChange(e.target.value)} />
          )} />
          <Controller name="fillColor" control={control} render={({ field }) => (
            <TextField size="small" {...field} inputProps={{ style:{ fontSize:12 } }} sx={{ flex:1 }} />
          )} />
        </Row>
        {isPoint && (
          <Row>
            <Lbl c="Radius" />
            <Controller name="circleRadius" control={control} render={({ field }) => (
              <Slider size="small" min={1} max={30}
                defaultValue={field.value}
                onChangeCommitted={(_, v) => field.onChange(v)}
                sx={{ flex:1, mx:1 }} />
            )} />
          </Row>
        )}
      </>)}

      <Sec c="Stroke" />
      <Row>
        <Lbl c="Color" />
        <Controller name="strokeColor" control={control} render={({ field }) => (
          <input type="color" style={S.colorPicker}
            defaultValue={field.value?.slice(0,7)}
            onBlur={e => field.onChange(e.target.value)} />
        )} />
        <Controller name="strokeColor" control={control} render={({ field }) => (
          <TextField size="small" {...field} inputProps={{ style:{ fontSize:12 } }} sx={{ flex:1 }} />
        )} />
      </Row>
      <Row>
        <Lbl c="Width" />
        <Controller name="strokeWidth" control={control} render={({ field }) => (
          <Slider size="small" min={0.5} max={20} step={0.5}
            defaultValue={field.value}
            onChangeCommitted={(_, v) => field.onChange(v)}
            sx={{ flex:1, mx:1 }} />
        )} />
      </Row>

      {!isPoint && (<>
        <Sec c="Line Style" />
        <Row>
          <Lbl c="Cap" />
          <Controller name="lineCap" control={control} render={({ field }) => (
            <ToggleButtonGroup size="small" exclusive value={field.value}
              onChange={(_, v) => { if (v) field.onChange(v); }}>
              {LINE_CAPS.map(c => <ToggleButton key={c} value={c} style={{ fontSize:11, padding:"2px 10px" }}>{c}</ToggleButton>)}
            </ToggleButtonGroup>
          )} />
        </Row>
        <Row>
          <Lbl c="Join" />
          <Controller name="lineJoin" control={control} render={({ field }) => (
            <ToggleButtonGroup size="small" exclusive value={field.value}
              onChange={(_, v) => { if (v) field.onChange(v); }}>
              {LINE_JOINS.map(j => <ToggleButton key={j} value={j} style={{ fontSize:11, padding:"2px 10px" }}>{j}</ToggleButton>)}
            </ToggleButtonGroup>
          )} />
        </Row>
        <Row>
          <Controller name="dashEnabled" control={control} render={({ field }) => (
            <FormControlLabel
              control={<Switch size="small" checked={field.value} onChange={e => field.onChange(e.target.checked)} />}
              label={<span style={{ fontSize:12 }}>Dashed</span>} />
          )} />
        </Row>
        {dashEnabled && (
          <Row>
            <Lbl c="Dash / Gap" />
            <Controller name="dashLength" control={control} render={({ field }) => (
              <TextField size="small" type="number" label="Dash" {...field}
                onChange={e => field.onChange(Number(e.target.value))}
                inputProps={{ style:{ fontSize:12 } }} sx={{ width:72 }} />
            )} />
            <Controller name="dashGap" control={control} render={({ field }) => (
              <TextField size="small" type="number" label="Gap" {...field}
                onChange={e => field.onChange(Number(e.target.value))}
                inputProps={{ style:{ fontSize:12 } }} sx={{ width:72 }} />
            )} />
          </Row>
        )}
      </>)}
    </div>
  );
});

// ─────────────────────────────────────────────────────────
// LABELLING TAB
// ─────────────────────────────────────────────────────────
const LabellingTab = memo(({ columns, control, setValue }) => {
  const exprRef = useRef(null);

  // watch only structural toggles
  const lblEnabled   = useWatch({ control, name: "lblEnabled" });
  const lblMode      = useWatch({ control, name: "lblMode" });
  const haloEnabled  = useWatch({ control, name: "haloEnabled" });

  const insertCol = useCallback((col) => {
    const el = exprRef.current;
    if (!el) return;
    const snippet = `["get","${col}"]`;
    const start   = el.selectionStart ?? el.value.length;
    const end     = el.selectionEnd   ?? el.value.length;
    const next    = el.value.slice(0, start) + snippet + el.value.slice(end);
    el.value      = next;
    setValue("lblTextValue", next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + snippet.length, start + snippet.length);
    });
  }, [setValue]);

  return (
    <div style={S.scrollBox}>
      <Row>
        <Controller name="lblEnabled" control={control} render={({ field }) => (
          <FormControlLabel
            control={<Switch size="small" checked={field.value} onChange={e => field.onChange(e.target.checked)} />}
            label={<span style={{ fontSize:13, fontWeight:600 }}>Enable Labels</span>} />
        )} />
      </Row>

      {lblEnabled && (<>
        <Sec c="Text Source" />
        <Row>
          <Controller name="lblMode" control={control} render={({ field }) => (
            <ToggleButtonGroup size="small" exclusive value={field.value}
              onChange={(_, v) => { if (v) { field.onChange(v); setValue("lblTextValue", ""); } }}>
              {["attribute","expression","literal"].map(m => (
                <ToggleButton key={m} value={m} style={{ fontSize:11, padding:"2px 12px" }}>{m}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )} />
        </Row>

        {lblMode === "attribute" ? (
          <Controller name="lblTextValue" control={control} render={({ field }) => (
            <FormControl fullWidth size="small" sx={{ mb:1 }}>
              <InputLabel style={{ fontSize:12 }}>Column</InputLabel>
              <Select label="Column" value={field.value} onChange={e => field.onChange(e.target.value)}
                MenuProps={{ style:{ zIndex:2500 } }} style={{ fontSize:12 }}>
                {columns.map(c => <MenuItem key={c} value={c} style={{ fontSize:12 }}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          )} />

        ) : lblMode === "expression" ? (
          <Controller name="lblTextValue" control={control} render={({ field }) => (
            <div style={{ marginBottom:8 }}>
              <textarea
                ref={exprRef}
                defaultValue={field.value}
                onChange={e => field.onChange(e.target.value)}
                placeholder={'["get","name"]'}
                rows={3}
                style={{
                  width:"100%", boxSizing:"border-box",
                  fontSize:11, fontFamily:"monospace",
                  border:"1px solid #ccc", borderRadius:4,
                  padding:"6px 8px", resize:"vertical", marginBottom:4,
                }}
              />
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {columns.map(c => (
                  <Chip key={c} label={c} size="small" clickable
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => insertCol(c)}
                    style={{ fontSize:10, height:20 }} />
                ))}
              </div>
            </div>
          )} />

        ) : (
          <Controller name="lblTextValue" control={control} render={({ field }) => (
            <TextField fullWidth size="small" placeholder="Static text" {...field}
              inputProps={{ style:{ fontSize:12 } }} sx={{ mb:1 }} />
          )} />
        )}

        <Sec c="Font" />
        <Row>
          <Controller name="fontFamily" control={control} render={({ field }) => (
            <FormControl size="small" sx={{ flex:1 }}>
              <InputLabel style={{ fontSize:12 }}>Family</InputLabel>
              <Select label="Family" value={field.value} onChange={e => field.onChange(e.target.value)}
                MenuProps={{ style:{ zIndex:2500 } }} style={{ fontSize:12 }}>
                {FONT_FAMILIES.map(f => <MenuItem key={f} value={f} style={{ fontSize:12 }}>{f}</MenuItem>)}
              </Select>
            </FormControl>
          )} />
          <Controller name="fontSize" control={control} render={({ field }) => (
            <FormControl size="small" sx={{ width:80 }}>
              <InputLabel style={{ fontSize:12 }}>Size</InputLabel>
              <Select label="Size" value={field.value} onChange={e => field.onChange(e.target.value)}
                MenuProps={{ style:{ zIndex:2500 } }} style={{ fontSize:12 }}>
                {FONT_SIZES.map(s => <MenuItem key={s} value={s} style={{ fontSize:12 }}>{s}px</MenuItem>)}
              </Select>
            </FormControl>
          )} />
        </Row>
        <Row>
          <Controller name="fontStyle" control={control} render={({ field }) => (
            <FormControl size="small" sx={{ flex:1 }}>
              <InputLabel style={{ fontSize:12 }}>Style</InputLabel>
              <Select label="Style" value={field.value} onChange={e => field.onChange(e.target.value)}
                MenuProps={{ style:{ zIndex:2500 } }} style={{ fontSize:12 }}>
                {FONT_STYLES.map(s => (
                  <MenuItem key={s} value={s} style={{ fontSize:12, fontStyle:s.includes("italic")?"italic":"normal", fontWeight:s.includes("bold")?700:400 }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )} />
          <Lbl c="Color" />
          <Controller name="fontColor" control={control} render={({ field }) => (
            <input type="color" style={S.colorPicker}
              defaultValue={field.value}
              onBlur={e => field.onChange(e.target.value)} />
          )} />
        </Row>

        <Sec c="Placement" />
        <Row>
          <Lbl c="Mode" />
          <Controller name="placement" control={control} render={({ field }) => (
            <ToggleButtonGroup size="small" exclusive value={field.value}
              onChange={(_, v) => { if (v) field.onChange(v); }}>
              {TEXT_PLACEMENTS.map(p => <ToggleButton key={p} value={p} style={{ fontSize:11, padding:"2px 12px" }}>{p}</ToggleButton>)}
            </ToggleButtonGroup>
          )} />
        </Row>
        <Row>
          <Lbl c="Align" />
          <Controller name="textAlign" control={control} render={({ field }) => (
            <ToggleButtonGroup size="small" exclusive value={field.value}
              onChange={(_, v) => { if (v) field.onChange(v); }}>
              {TEXT_ALIGNS.map(a => <ToggleButton key={a} value={a} style={{ fontSize:10, padding:"2px 7px" }}>{a}</ToggleButton>)}
            </ToggleButtonGroup>
          )} />
        </Row>
        <Row>
          <Lbl c="Baseline" />
          <Controller name="baseline" control={control} render={({ field }) => (
            <FormControl size="small" sx={{ flex:1 }}>
              <Select value={field.value} onChange={e => field.onChange(e.target.value)}
                MenuProps={{ style:{ zIndex:2500 } }} style={{ fontSize:12 }}>
                {TEXT_BASELINES.map(b => <MenuItem key={b} value={b} style={{ fontSize:12 }}>{b}</MenuItem>)}
              </Select>
            </FormControl>
          )} />
        </Row>

        <Sec c="Offset & Rotation" />
        <Row>
          <Lbl c="Offset X / Y" />
          <Controller name="offsetX" control={control} render={({ field }) => (
            <TextField size="small" type="number" label="X" {...field}
              onChange={e => field.onChange(Number(e.target.value))}
              inputProps={{ style:{ fontSize:12 } }} sx={{ width:70 }} />
          )} />
          <Controller name="offsetY" control={control} render={({ field }) => (
            <TextField size="small" type="number" label="Y" {...field}
              onChange={e => field.onChange(Number(e.target.value))}
              inputProps={{ style:{ fontSize:12 } }} sx={{ width:70 }} />
          )} />
        </Row>
        <Row>
          <Lbl c="Rotation°" />
          <Controller name="rotation" control={control} render={({ field }) => (
            <Slider size="small" min={-180} max={180}
              defaultValue={field.value}
              onChangeCommitted={(_, v) => field.onChange(v)}
              sx={{ flex:1, mx:1 }} />
          )} />
        </Row>

        <Sec c="Halo" />
        <Row>
          <Controller name="haloEnabled" control={control} render={({ field }) => (
            <FormControlLabel
              control={<Switch size="small" checked={field.value} onChange={e => field.onChange(e.target.checked)} />}
              label={<span style={{ fontSize:12 }}>Enable</span>} />
          )} />
        </Row>
        {haloEnabled && (
          <Row>
            <Lbl c="Color" />
            <Controller name="haloColor" control={control} render={({ field }) => (
              <input type="color" style={S.colorPicker}
                defaultValue={field.value}
                onBlur={e => field.onChange(e.target.value)} />
            )} />
            <Lbl c="Width" />
            <Controller name="haloWidth" control={control} render={({ field }) => (
              <Slider size="small" min={1} max={10}
                defaultValue={field.value}
                onChangeCommitted={(_, v) => field.onChange(v)}
                sx={{ flex:1, mx:1 }} />
            )} />
          </Row>
        )}
        <Row>
          <Controller name="overflow" control={control} render={({ field }) => (
            <FormControlLabel
              control={<Switch size="small" checked={field.value} onChange={e => field.onChange(e.target.checked)} />}
              label={<span style={{ fontSize:12 }}>Allow overflow</span>} />
          )} />
        </Row>
      </>)}
    </div>
  );
});

// ─────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────
export default function LayerStyleDialog({ vectorLayer, onClose }) {
  const [tab, setTab]     = useState(0);
  const dialogRef         = useRef(null);
  const originalStyle     = useRef(vectorLayer?.getStyle?.() ?? null);
  const onDragDown        = useDrag(dialogRef);

  const geomType = useMemo(() => vectorLayer?.get("geometry_type") ?? "", [vectorLayer]);
  const columns  = useMemo(() => getColumns(vectorLayer), [vectorLayer]);

  // useForm — defaultValues loaded from vectorLayer on mount
  const { control, getValues, setValue, reset } = useForm({
    defaultValues: savedToValues(vectorLayer?.get("style_state")),
  });

  // Reset form when vectorLayer changes (new layer opened)
  const prevLayerRef = useRef(vectorLayer);
  if (prevLayerRef.current !== vectorLayer) {
    prevLayerRef.current = vectorLayer;
    reset(savedToValues(vectorLayer?.get("style_state")));
  }

  // Apply — getValues() reads the whole form in one shot, no state involved
  const handleApply = useCallback(() => {
    vectorLayer?.setStyle(buildFlatStyle(geomType, getValues()));
  }, [vectorLayer, geomType, getValues]);

  // OK — same read, persist, close
  const handleOk = useCallback(() => {
    const v = getValues();
    vectorLayer?.setStyle(buildFlatStyle(geomType, v));
    vectorLayer?.set("style_state", v);
    onClose?.();
  }, [vectorLayer, geomType, getValues, onClose]);

  // Cancel — restore original style, close
  const handleCancel = useCallback(() => {
    vectorLayer?.setStyle(originalStyle.current ?? null);
    onClose?.();
  }, [vectorLayer, onClose]);

  if (!vectorLayer) return null;

  return (
    <div ref={dialogRef} style={S.dialog}>
      <div style={S.titleBar} onMouseDown={onDragDown}>
        <span style={S.titleText}>
          Layer Style <span style={S.titleSub}>{geomType || "Unknown"}</span>
        </span>
        <IconButton size="small" style={S.closeBtn}
          onMouseDown={e => e.stopPropagation()} onClick={handleCancel}>
          <CloseIcon style={{ fontSize:16 }} />
        </IconButton>
      </div>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{
          minHeight:36, flexShrink:0,
          borderBottom:"1px solid #e0e0e0", background:"#f5f7fa",
          "& .MuiTab-root":{ fontSize:12, minHeight:36, py:0, fontWeight:500 },
          "& .Mui-selected":{ color:"#1976d2" },
        }}
        TabIndicatorProps={{ style:{ height:2, background:"#1976d2" } }}>
        <Tab label="Symbology" />
        <Tab label="Labelling" />
      </Tabs>

      <div style={S.tabPanel}>
        <div style={tab === 0 ? S.tabSlot : S.tabSlotHidden}>
          <SymbologyTab geomType={geomType} control={control} />
        </div>
        <div style={tab === 1 ? S.tabSlot : S.tabSlotHidden}>
          <LabellingTab columns={columns} control={control} setValue={setValue} />
        </div>
      </div>

      <div style={S.actionBar}>
        <Button size="small" onClick={handleCancel} style={S.btnText} color="inherit">Cancel</Button>
        <Button size="small" variant="outlined" onClick={handleApply} style={S.btnText}>Apply</Button>
        <Button size="small" variant="contained" onClick={handleOk} style={S.btnOk}>OK</Button>
      </div>
    </div>
  );
}